/**
 * One doc per RECORD, synced through OPBridge's doc store (S305, 2026-10-06).
 *
 * WHY. OPBridge copies documents between PCs only for allow-listed ids, and
 * replicates a document as ONE newest-wins record (their DEC-554). The Matrix
 * and Rig States were each ONE document holding a whole library, so two PCs
 * editing inside one sync window lost one side outright, which is why neither
 * was on the allow-list and neither travelled at all. Split into one document
 * per record, whole-document newest-wins becomes per-record newest-wins: a
 * slot claimed on the dev PC and a look saved on the Mac in the same minute
 * both survive. The same reasoning, and the same shape, as
 * `layout/projectSync.ts` (one doc per project, 2026-08-31).
 *
 * This file is the engine, deliberately free of Solid and localStorage, so a
 * smoke can run two simulated devices on the REAL engine against one
 * in-memory doc store (`record_doc_sync_two_devices`). The wiring (signals,
 * timers, the kill switch, `doc_updated`) is `installRecordSync` at the end.
 *
 * ── THE MERGE, PER RECORD ────────────────────────────────────────────
 *
 * Each record has three versions: LOCAL (this device's library), REMOTE (the
 * bridge's doc) and AGREED (the content and rev this device and the bridge
 * last held in common, persisted). Comparing against AGREED says exactly who
 * changed what, with no clocks:
 *   - only local changed  -> push;     only remote changed -> adopt;
 *   - local deleted, remote unchanged -> DELETE the doc;
 *   - remote deleted, local unchanged -> remove locally;
 *   - deleted on one side, EDITED on the other -> the edit wins (losing
 *     authored work is the worse error; projectMerge.ts's rule);
 *   - both edited -> newer `modifiedAt` wins (the one place a clock decides,
 *     and the one place a side is lost, as with projects - DEC-081).
 *
 * ── MIGRATION FROM THE WHOLE-LIBRARY DOC ─────────────────────────────
 *
 * The old single doc (`matrix` / `rigstate`) is READ, never written or
 * deleted, by this build: an older build on another device keeps working,
 * and a bad split stays recoverable from it (S305 plan, step 1; removing it
 * is a later release's decision). It is used two ways, both only while this
 * device has no agreed state yet:
 *   - no per-record docs on the bridge: its records are UNIONED into the
 *     local library (local wins an id both hold) and everything is pushed.
 *     Nothing is lost; at worst a deletion made offline comes back.
 *   - per-record docs already there (another device migrated first): it is
 *     this device's AGREED baseline. Under the old sync every connect made
 *     local equal to it, so a record that still equals it and is missing
 *     from the bridge was deleted elsewhere and is removed here, while a
 *     record edited or added offline is pushed.
 */

export interface DocStoreLike {
  list(): Promise<{ doc_id: string; rev: number }[]>;
  get(docId: string): Promise<{ rev: number; body: unknown }>;
  put(docId: string, baseRev: number, body: unknown): Promise<{ rev: number }>;
  del(docId: string): Promise<void>;
}

export interface RecordAdapter<L> {
  /** Display name in toasts and logs, e.g. "Matrix". */
  label: string;
  /** Doc id prefixes this adapter owns, e.g. `matrix-device-`. */
  prefixes: string[];
  /** The pre-split whole-library doc id. */
  legacyDocId: string;
  /** The library as records, keyed by doc id. */
  split(lib: L): Map<string, unknown>;
  /** A new library with `puts` placed and `dels` removed. Pure. A record it
   *  cannot place (a look whose bank has not arrived) is simply left out;
   *  the engine notices and retries it next pass. */
  apply(lib: L, puts: Map<string, unknown>, dels: Set<string>): L;
  /** Null when a remote record is usable, else why not. A refused record is
   *  never applied and never overwritten by our copy. */
  problem(docId: string, record: unknown): string | null;
  /** The library inside the legacy doc's body, or null. */
  legacyLibrary(body: unknown): L | null;
}

export interface AgreedEntry { rev: number | null; sum: string }
export interface StampEntry { sum: string; at: number }
/** Persisted per adapter. `agreed`: what this device and the bridge last
 *  held in common. `stamps`: when each local record last changed here. */
export interface SyncMemory {
  agreed: Record<string, AgreedEntry>;
  stamps: Record<string, StampEntry>;
  /** The legacy doc has been looked at once; never again. */
  legacyDone?: boolean;
  /** node_id of the bridge `agreed` was agreed with (audit 2026-11-03 B-10).
   *  Revs and "it was there, now it is gone" mean something only on that
   *  bridge. Null = not known yet (memory from before this field). */
  nodeId?: string | null;
}

export interface RecordDocBody { schema: 1; modifiedAt: number; record: unknown }

export interface SyncIo<L> {
  read(): L;
  write(next: L): void;
  memory: SyncMemory;
  saveMemory(m: SyncMemory): void;
  now(): number;
  /** True when the local library carries no authority of its own: an
   *  unedited first-run seed (`/matrix-seed.json`). The old whole-library
   *  sync overwrote it on the first connect; here it is treated as baseline,
   *  so a seed device the operator deleted long ago is not pushed back. */
  unauthoritative?(): boolean;
  /** Asked before every request: true stops the pass where it is (the
   *  wiring answers "performance mode came on"). A first pass on a real rig
   *  is ~750 writes, long enough for a show to start in the middle of it.
   *  What was done stays agreed; the next pass carries on from there. */
  shouldStop?(): boolean;
  /** node_id of the bridge this pass talks to, when known (B-10). */
  nodeId?: string | null;
}

export interface SyncReport {
  pushed: number;
  deletedRemote: number;
  adopted: number;
  removedLocal: number;
  skipped: { docId: string; reason: string }[];
  migrated: 'none' | 'seeded-bridge' | 'baseline';
  /** The pass stopped early on `shouldStop`. */
  stopped: boolean;
  /** The bridge listed none of our records while we had agreed on several:
   *  nothing was deleted or pushed (B-10). */
  held: boolean;
  /** This pass talked to another bridge than the one `agreed` came from, so it
   *  unioned instead of inferring deletions (B-10). */
  otherBridge: boolean;
}

/** JSON with object keys sorted at every depth, so two devices that build the
 *  same record in a different key order compare equal. */
export function canonJson(v: unknown): string {
  return JSON.stringify(sortKeys(v));
}
function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>).sort()) {
      const x = (v as Record<string, unknown>)[k];
      if (x !== undefined) out[k] = sortKeys(x);
    }
    return out;
  }
  return v;
}

/** A short digest of a value's canonical JSON. The memory keeps digests, not
 *  copies: it only ever asks "is this the same content", and the Matrix seed
 *  alone is 225 KB, which full copies in `agreed` and `stamps` would have
 *  tripled inside an iPad's ~5 MB of localStorage. Two independent 32-bit
 *  FNV-1a lanes plus the length; a false "same" needs a 64-bit collision at
 *  equal length. */
export function digest(v: unknown): string {
  const s = canonJson(v);
  let h1 = 0x811c9dc5, h2 = 0x01000193 ^ 0x5bd1e995;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995) ^ (h2 >>> 15);
  }
  return `${(h1 >>> 0).toString(16)}${(h2 >>> 0).toString(16)}:${s.length}`;
}

/** One doc-id segment from any string. OPBridge allows `[A-Za-z0-9_-]` only
 *  (`storage.rs::validate_consumer_id`); everything else, `_` included so the
 *  mapping stays one-to-one, becomes `_` + two hex digits. */
export function idPart(s: string): string {
  let out = '';
  for (const ch of s) {
    if (/[A-Za-z0-9-]/.test(ch)) out += ch;
    else for (const b of new TextEncoder().encode(ch)) out += `_${b.toString(16).padStart(2, '0')}`;
  }
  return out;
}

/** `server_rev` out of a 409's body (OPBridge `docs.rs`: `{ error, server_rev }`). */
function serverRevOf(err: unknown): number | null {
  const msg = err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
    ? (err as { message: string }).message : '';
  const m = /"server_rev"\s*:\s*(\d+)/.exec(msg);
  return m ? parseInt(m[1]!, 10) : null;
}

function statusOf(err: unknown): number | null {
  if (err && typeof err === 'object') {
    const e = err as { httpStatus?: unknown; status?: unknown };
    if (typeof e.httpStatus === 'number') return e.httpStatus;
    if (typeof e.status === 'number') return e.status;
  }
  return null;
}

/** Bring `memory.stamps` up to date with the local library: a record whose
 *  content moved since the last look gets `at = now`. A record seen for the
 *  first time with no stamp gets 0 (age unknown), so it loses a both-edited
 *  tie to any real remote edit - the old whole-library sync always let the
 *  remote win, and this keeps that for the one case nobody can date. */
export function refreshStamps(records: Map<string, string>, memory: SyncMemory, now: number, firstSight: 'zero' | 'now'): boolean {
  let moved = false;
  for (const [id, json] of records) {
    const s = memory.stamps[id];
    if (!s) { memory.stamps[id] = { sum: json, at: firstSight === 'now' ? now : 0 }; moved = true; }
    else if (s.sum !== json) { memory.stamps[id] = { sum: json, at: now }; moved = true; }
  }
  for (const id of Object.keys(memory.stamps)) {
    if (!records.has(id)) { delete memory.stamps[id]; moved = true; }
  }
  return moved;
}

function sumMap(records: Map<string, unknown>): Map<string, string> {
  const out = new Map<string, string>();
  for (const [id, r] of records) out.set(id, digest(r));
  return out;
}

/** One full pass. Throws only on a store failure that is not a per-doc 404 /
 *  409 (the caller reports it; the next pass retries). */
export async function syncOnce<L>(store: DocStoreLike, adapter: RecordAdapter<L>, io: SyncIo<L>): Promise<SyncReport> {
  const report: SyncReport = {
    pushed: 0, deletedRemote: 0, adopted: 0, removedLocal: 0, skipped: [], migrated: 'none', stopped: false,
    held: false, otherBridge: false,
  };
  const stop = (): boolean => (report.stopped ||= io.shouldStop?.() === true);
  const mem = io.memory;
  const now = io.now();
  const owns = (id: string) => adapter.prefixes.some((p) => id.startsWith(p));

  // ── 1. what the bridge holds (metadata only) ───────────────────────
  const listing = await store.list();
  const remote = new Map<string, number>();
  let legacyPresent = false;
  for (const d of listing) {
    if (d.doc_id === adapter.legacyDocId) legacyPresent = true;
    else if (owns(d.doc_id)) remote.set(d.doc_id, d.rev);
  }

  // ── 1b. the two guards on the one inference that DELETES (B-10) ─────
  // The same two as projectSync.ts (audit E-1), for the same reason: a
  // record agreed earlier and missing now is read as "deleted elsewhere" and
  // removed here. A wrong listing turned that into a wiped Matrix.
  //  - Another bridge than the one we agreed with (the default was switched,
  //    a fresh or reset OPBridge): its revs and its list say nothing about
  //    ours, so the agreement is dropped and this pass unions.
  //  - The bridge lists NONE of our records while we agreed on several: an
  //    emptied store or a failed read looks exactly like "all deleted", so
  //    the pass is HELD: nothing deleted, nothing pushed, agreement kept.
  const nodeNow = io.nodeId ?? null;
  if (mem.nodeId && nodeNow && mem.nodeId !== nodeNow) {
    mem.agreed = {};
    report.otherBridge = true;
  }
  if (nodeNow) mem.nodeId = nodeNow;
  if (remote.size === 0 && Object.keys(mem.agreed).length >= 2 && mem.legacyDone) {
    report.held = true;
    io.saveMemory(mem);
    return report;
  }

  // ── 2. migration, only while this device has agreed nothing yet ─────
  if (!mem.legacyDone && Object.keys(mem.agreed).length === 0) {
    let legacy: L | null = null;
    if (legacyPresent) {
      try { legacy = adapter.legacyLibrary((await store.get(adapter.legacyDocId)).body); } catch (err) {
        if (statusOf(err) !== 404) throw err;
      }
    }
    const seedOnly = io.unauthoritative?.() === true;
    if (remote.size === 0) {
      if (legacy && seedOnly) {
        // A first-run seed has nothing to add: the old doc is the rig.
        io.write(legacy);
        report.adopted += adapter.split(legacy).size;
        report.migrated = 'seeded-bridge';
      } else if (legacy) {
        const local = adapter.split(io.read());
        const add = new Map<string, unknown>();
        for (const [id, r] of adapter.split(legacy)) if (!local.has(id) && !adapter.problem(id, r)) add.set(id, r);
        if (add.size > 0) { io.write(adapter.apply(io.read(), add, new Set())); report.adopted += add.size; }
        report.migrated = 'seeded-bridge';
      }
    } else {
      // Baseline: what this device holds without having authored it. The
      // old doc wins an id both hold (it is what the old sync last agreed).
      const baseline = new Map<string, string>();
      if (seedOnly) for (const [id, json] of sumMap(adapter.split(io.read()))) baseline.set(id, json);
      if (legacy) for (const [id, json] of sumMap(adapter.split(legacy))) baseline.set(id, json);
      for (const [id, json] of baseline) mem.agreed[id] = { rev: null, sum: json };
      if (baseline.size > 0) report.migrated = 'baseline';
    }
  }
  // Once this device has looked (or there was nothing to look at), the legacy
  // doc is never read again: an old build still writing it must not keep
  // re-seeding anything.
  mem.legacyDone = true;

  const localRecs = adapter.split(io.read());
  const local = sumMap(localRecs);
  // Age unknown (0) only for what was already here on this device's very
  // first pass; a record that appears after that was made here, now.
  const firstPass = Object.keys(mem.stamps).length === 0 && Object.keys(mem.agreed).every((id) => mem.agreed[id]!.rev === null);
  refreshStamps(local, mem, now, firstPass ? 'zero' : 'now');

  // ── 3. decide, per record ──────────────────────────────────────────
  const adopt = new Map<string, { record: unknown; rev: number; sum: string }>();
  const removeLocal = new Set<string>();
  const push = new Map<string, { baseRev: number; sum: string }>();
  const deleteRemote = new Map<string, number>();

  const ids = new Set<string>([...local.keys(), ...Object.keys(mem.agreed), ...remote.keys()]);
  for (const id of ids) {
    const l = local.get(id);
    const a = mem.agreed[id];
    const r = remote.get(id);

    if (r !== undefined) {
      if (a && a.rev === r) {
        // The bridge has not moved since we agreed.
        if (l === undefined) deleteRemote.set(id, r);
        else if (l !== a.sum) push.set(id, { baseRev: r, sum: l });
        continue;
      }
      // The bridge moved, or we never agreed: read it.
      if (stop()) break;
      let body: RecordDocBody | null = null;
      try { body = (await store.get(id)).body as RecordDocBody; } catch (err) {
        if (statusOf(err) === 404) continue; // deleted between list and get: next pass
        throw err;
      }
      const rec = body && typeof body === 'object' ? body.record : undefined;
      const why = rec === undefined ? 'no record in the document' : adapter.problem(id, rec);
      if (why) { report.skipped.push({ docId: id, reason: why }); continue; }
      const bj = digest(rec);
      if (l === bj) { mem.agreed[id] = { rev: r, sum: bj }; continue; }
      if (l === undefined) {
        // Gone here. Deleted by us with the bridge unchanged in content -> delete it;
        // otherwise (new to us, or edited there after our delete) -> it comes back.
        if (a && a.sum === bj) deleteRemote.set(id, r);
        else adopt.set(id, { record: rec, rev: r, sum: bj });
        continue;
      }
      if (a && l === a.sum) { adopt.set(id, { record: rec, rev: r, sum: bj }); continue; }
      // Both sides changed it (or we never agreed and they differ).
      const localAt = mem.stamps[id]?.at ?? 0;
      const remoteAt = typeof body!.modifiedAt === 'number' ? body!.modifiedAt : 0;
      if (remoteAt > localAt) adopt.set(id, { record: rec, rev: r, sum: bj });
      else push.set(id, { baseRev: r, sum: l });
      continue;
    }

    // Not on the bridge.
    if (a) {
      if (l !== undefined && l !== a.sum) push.set(id, { baseRev: 0, sum: l }); // edit beats delete
      else if (l !== undefined) removeLocal.add(id);                             // deleted elsewhere
      else delete mem.agreed[id];
    } else if (l !== undefined) {
      push.set(id, { baseRev: 0, sum: l });
    }
  }

  // ── 4. apply what arrived ──────────────────────────────────────────
  if (adopt.size > 0 || removeLocal.size > 0) {
    const puts = new Map<string, unknown>([...adopt].map(([id, x]) => [id, x.record]));
    const next = adapter.apply(io.read(), puts, removeLocal);
    io.write(next);
    const after = adapter.split(next);
    for (const [id, x] of adopt) {
      // Placed -> agreed at the bridge's content. Not placed (its parent is
      // not here yet) -> left unagreed, so the next pass reads it again.
      if (after.has(id)) { mem.agreed[id] = { rev: x.rev, sum: x.sum }; report.adopted += 1; }
    }
    for (const id of removeLocal) {
      if (!after.has(id)) { delete mem.agreed[id]; report.removedLocal += 1; }
    }
    // Anything the apply changed, adopted or renumbered, is stamped now. The
    // stamp only ever decides a both-edited conflict, and an adopted record
    // equal to its agreed copy can never be in one.
    refreshStamps(sumMap(after), mem, now, 'now');
  }

  // ── 5. push and delete ─────────────────────────────────────────────
  for (const [id, p] of push) {
    if (stop()) break;
    const rec = localRecs.get(id);
    const body: RecordDocBody = { schema: 1, modifiedAt: mem.stamps[id]?.at || now, record: rec };
    try {
      const res = await store.put(id, p.baseRev, body);
      mem.agreed[id] = { rev: res.rev, sum: p.sum };
      report.pushed += 1;
    } catch (err) {
      // 409: someone wrote it since our list. Forget the rev; the next pass
      // reads their version and merges instead of clobbering it.
      if (statusOf(err) !== 409) throw err;
      // ...unless it was DELETED: OPBridge keeps a hidden tombstone at a rev
      // above 0, so re-claiming a deleted matrix square (`matrix-slot-cc-1-74`
      // is the same id every time) was refused on every pass and never reached
      // the other PCs (2026-11-03 audit E-2). A tombstone reads 404; only then
      // is it re-created, at the rev the 409 named.
      const serverRev = p.baseRev === 0 ? serverRevOf(err) : null;
      if (serverRev !== null) {
        const tombstoned = await store.get(id).then(() => false, (e: unknown) => statusOf(e) === 404);
        if (tombstoned) {
          try {
            const res = await store.put(id, serverRev, body);
            mem.agreed[id] = { rev: res.rev, sum: p.sum };
            report.pushed += 1;
            continue;
          } catch (e2) {
            if (statusOf(e2) !== 409) throw e2;
          }
        }
      }
      if (mem.agreed[id]) mem.agreed[id] = { rev: -1, sum: mem.agreed[id]!.sum };
    }
  }
  for (const [id] of deleteRemote) {
    if (stop()) break;
    try {
      await store.del(id);
      delete mem.agreed[id];
      report.deletedRemote += 1;
    } catch (err) {
      if (statusOf(err) === 404) { delete mem.agreed[id]; continue; }
      throw err;
    }
  }

  io.saveMemory(mem);
  return report;
}

